import * as service from '../services/api';
import { MembershipSourceType, PromoType } from '../types/register';
import { products, ProductType } from '../components/Registration/data';
import { convertUnitAmountToAmount } from '../components/Common/helperFunction';

export const getRegistrationType = (discount: number, isCardCollected: boolean): string => {
  const isDiscountedCompletely = discount === 100
  const isPartialDiscount = discount > 0 && discount < 100

  if (isDiscountedCompletely && !isCardCollected) {
    return 'CompletelyDiscountedWithCardCollection'
  }
  if (isDiscountedCompletely && isCardCollected) {
    return 'CompletelyDiscountedWithoutCardCollection'
  }
  if (isPartialDiscount) {
    return 'PartialDiscountWithCardCollection'
  }
  return 'StandardCardCollection'
}

export const getSelectedPriceHasPromotion = (promo: PromoType | undefined, product: string): boolean => {
  const hasPromotion = !!(promo && promo.products && (promo.products.length === 0 || promo.products.includes(product)))
  return hasPromotion
}

export const calculateActualAmount = (hasPromo: boolean, price: number, percentOff = 0, amountOff = 0): number => {
  const amount = hasPromo ? (price - (amountOff ? amountOff / 100 : 0)) * (1 - (percentOff ?? 0) / 100) : price
  const actualAmount = amount ? Number.parseFloat(amount.toFixed(2)) : 0
  return actualAmount
}

export const getMonthsText = (durationInMonths = -1): string => durationInMonths === 1 ? 'Month' : 'Months'
export const getYearsText = (durationInYears = -1): string => durationInYears === 1 ? 'Year' : 'Years'

export const getBillingCycleText = (billingCycle: string | undefined): string | undefined =>
  billingCycle === '3 Months' ? 'quarterly' : billingCycle === '12 Months' ? 'annually' : billingCycle;

export const getRegistrationDetailSummary = (type: string, billingCycle: string, durationMonths: number, selectedPrice: number, actualPrice: number): string => {
  const billingFrequency = getBillingCycleText(billingCycle)
  const monthsText = getMonthsText(durationMonths)
  const recurringMonthText = `${durationMonths === 1 ? '' : durationMonths} ${monthsText}`
  const options: Record<string, string> = {
    PartialDiscountWithCardCollection: `After your initial discounted membership rate of $${actualPrice}, you will be subscribing to a recurring $${selectedPrice} ${billingFrequency} membership fee. This charge will automatically renew every ${recurringMonthText}.`,
    CompletelyDiscountedWithCardCollection: `You will be subscribing to a free membership for ${durationMonths} ${monthsText}. After your initial free membership, you will be subscribing to a recurring $${selectedPrice} ${billingFrequency} membership fee. This charge will automatically renew every ${recurringMonthText}.`,
    CompletelyDiscountedWithoutCardCollection: `You will be subscribing to a free membership for ${durationMonths} ${monthsText}.`,
    StandardCardCollection: `You will be charged a recurring $${selectedPrice} ${billingFrequency} membership fee. This charge will automatically renew every ${recurringMonthText}.`,
  }
  return options[type]
}

export const checkEmail = async (email: string): Promise<boolean> => {
  const emailResponse: any = await fetch(service.API_ENDPOINTS.checkEmailAvailable, {
    method: 'POST',
    body: JSON.stringify({
      email,
    }),
    headers: { 'Content-type': 'application/json' },
  })
  const result = await emailResponse.json()
  return result.emailAvailable
}

export const getMembershipSource = (transactionReceipt = 'NA'): MembershipSourceType => {
  if (transactionReceipt.startsWith('{')) return 'Google'
  if (transactionReceipt.startsWith('sub')) return 'Stripe'
  if (transactionReceipt.startsWith('I-')) return 'PayPal'
  return 'Apple'
}

export const getSelectedPrice = (membershipSource: MembershipSourceType, subscriptionValue: string): ProductType => {
  if (membershipSource === 'Stripe') {
    return products.find((product) => product.id === subscriptionValue) ?? products[0]
  }
  if (subscriptionValue.includes('year')) return products[0]
  return products[1]
}

export const getSelectedPriceFromProducts = (membershipSource: MembershipSourceType, subscriptionValue: string, productsList: ProductType[]): ProductType => {
  if (membershipSource === 'Stripe' && productsList) {
    const planItem = productsList.find((product) => product.id === subscriptionValue)
    return planItem?.recurring?.find((recurringItem) => recurringItem.interval === 'month') ? products[1] : products[0];
  }
  if (subscriptionValue.includes('year')) return products[0]
  return products[1]
}

export const getPlanPrice = (amountOff: number, percentOff: number, planPrice: number): number => {
  let calculatedPrice = 0;
  const hasDiscount = amountOff || percentOff;
  if (hasDiscount) {
    calculatedPrice = (planPrice - (amountOff ? amountOff / 100 : 0)) * (1 - (percentOff ?? 0) / 100);
  }
  if (!hasDiscount) {
    calculatedPrice = planPrice;
  }
  return calculatedPrice;
}
